var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk } from "@reduxjs/toolkit";
import { usersApi } from "../../../api/enpoints/users-api";
// Helper function to clean filter values
const cleanFilters = (filters) => {
    const cleaned = {};
    Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (Array.isArray(value)) {
            cleaned[key] = value.length > 0 ? value : undefined;
        }
        else if (value === "" || value === null) {
            cleaned[key] = undefined;
        }
        else if (value !== undefined) {
            cleaned[key] = value;
        }
    });
    return cleaned;
};
// Get all users thunk
export const getAllUsers = createAsyncThunk("users/getAllUsers", (query_1, _a) => __awaiter(void 0, [query_1, _a], void 0, function* (query, { rejectWithValue }) {
    try {
        // Clean the filters before sending to API
        const cleanedQuery = Object.assign(Object.assign({}, query), { filters: cleanFilters(query.filters) });
        const response = yield usersApi.getAllUsers(cleanedQuery);
        if (response.data.isSuccess && response.data.data) {
            return response.data.data; // Return the paginated data
        }
        else {
            return rejectWithValue(response.data.message || "Failed to fetch users");
        }
    }
    catch (error) {
        return rejectWithValue(error);
    }
}));
// Create new user thunk
export const createNewUser = createAsyncThunk("users/createNewUser", (userData_1, _a) => __awaiter(void 0, [userData_1, _a], void 0, function* (userData, { rejectWithValue }) {
    var _b, _c;
    try {
        const response = yield usersApi.createNewUser(userData);
        if (response.data.isSuccess && response.data.data) {
            return response.data;
        }
        else {
            return rejectWithValue(response.data.message || "Failed to create user");
        }
    }
    catch (error) {
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) || (error === null || error === void 0 ? void 0 : error.message) || "Failed to create user");
    }
}));
// Update user thunk
export const updateUser = createAsyncThunk("users/updateUser", (userData_1, _a) => __awaiter(void 0, [userData_1, _a], void 0, function* (userData, { rejectWithValue }) {
    try {
        const response = yield usersApi.updateUser(userData);
        if (response.data.isSuccess && response.data.data) {
            return response.data.data;
        }
        else {
            return rejectWithValue(response.data.message || "Failed to update user");
        }
    }
    catch (error) {
        return rejectWithValue(error);
    }
}));
// Delete user thunk
export const deleteUser = createAsyncThunk("users/deleteUser", (id_1, _a) => __awaiter(void 0, [id_1, _a], void 0, function* (id, { rejectWithValue }) {
    try {
        const response = yield usersApi.deleteUser(id);
        if (response.data.isSuccess) {
            return {
                id,
                message: response.data.message || "User deleted successfully",
            };
        }
        else {
            return rejectWithValue(response.data.message || "Failed to delete user");
        }
    }
    catch (error) {
        return rejectWithValue(error);
    }
}));
// Toggle user status thunk
export const toggleUserStatus = createAsyncThunk("users/toggleUserStatus", (data_1, _a) => __awaiter(void 0, [data_1, _a], void 0, function* (data, { rejectWithValue }) {
    var _b, _c;
    try {
        const response = yield usersApi.toggleUserStatus(data);
        if (response.data.isSuccess) {
            return {
                id: data.id,
                isActive: data.isActive,
                message: response.data.message || "User status updated successfully",
            };
        }
        else {
            return rejectWithValue(response.data.message || "Failed to update user status");
        }
    }
    catch (error) {
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
            (error === null || error === void 0 ? void 0 : error.message) ||
            "Failed to update user status");
    }
}));
// Get active users for dropdown thunk
export const getActiveUsers = createAsyncThunk("users/getActiveUsers", (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    var _b, _c;
    try {
        const response = yield usersApi.getActiveUsers();
        if (response.data.isSuccess && response.data.data) {
            return response.data.data;
        }
        else {
            return rejectWithValue(response.data.message || "Failed to fetch active users");
        }
    }
    catch (error) {
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
            (error === null || error === void 0 ? void 0 : error.message) ||
            "Failed to fetch active users");
    }
}), {
    condition: (_, { getState }) => {
        const { user } = getState();
        // Prevent duplicate calls while already loading
        if (user.activeUsersLoading)
            return false;
    },
});
// Get user profile thunk
export const getUserProfile = createAsyncThunk("users/getUserProfile", (userId_1, _a) => __awaiter(void 0, [userId_1, _a], void 0, function* (userId, { rejectWithValue }) {
    var _b, _c;
    try {
        const response = yield usersApi.getUserProfile(userId);
        if (response.data.isSuccess && response.data.data) {
            const profile = response.data.data;
            return profile;
        }
        else {
            console.error("getUserProfile failed:", response.data.message);
            return rejectWithValue(response.data.message || "Failed to fetch user profile");
        }
    }
    catch (error) {
        console.error("getUserProfile error:", error);
        return rejectWithValue(((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
            (error === null || error === void 0 ? void 0 : error.message) ||
            "Failed to fetch user profile");
    }
}));
